import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { environment } from '../environment/environment';

interface CredentialResponse {
  credential: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: CredentialResponse) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              type?: 'standard' | 'icon';
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              width?: number;
            }
          ) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleSignInService {
  private webInitialized = false;

  private get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  private getCurrentTheme(): 'light' | 'dark' {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }

  renderButton(element: HTMLElement, onSuccess: (idToken: string) => void, onError?: (err: unknown) => void): void {
    if (this.isNative) {
      this.renderNativeButton(element, onSuccess, onError);
      return;
    }
    this.renderWebButton(element, onSuccess);
  }

  private nativeInitialized = false;

  private async initNative(): Promise<void> {
    if (this.nativeInitialized) return;
    await GoogleAuth.initialize({
      scopes: ['profile', 'email'],
      grantOfflineAccess: true,
    });
    this.nativeInitialized = true;
  }

  async signOut(): Promise<void> {
    if (this.isNative) {
      if (this.nativeInitialized) {
        try {
          await GoogleAuth.signOut();
        } catch {
          // ignore — session already gone
        }
      }
    } else {
      window.google?.accounts?.id?.disableAutoSelect?.();
    }
  }

  private renderNativeButton(element: HTMLElement, onSuccess: (idToken: string) => void, onError?: (err: unknown) => void): void {
    const isDark = this.getCurrentTheme() === 'dark';
    element.innerHTML = '';

    const btn = document.createElement('button');
    btn.type = 'button';
    Object.assign(btn.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      width: '100%',
      height: '44px',
      padding: '0 16px',
      border: isDark ? 'none' : '1px solid #dadce0',
      borderRadius: '4px',
      background: isDark ? '#131314' : '#fff',
      color: isDark ? '#e3e3e3' : '#3c4043',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: 'Roboto, sans-serif',
      cursor: 'pointer',
      boxSizing: 'border-box',
    });

    btn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
        <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
      </svg>
      Continuar com Google
    `;

    btn.addEventListener('click', async () => {
      try {
        btn.disabled = true;
        await this.initNative();
        const user = await GoogleAuth.signIn();
        const idToken = user.authentication.idToken;
        if (!idToken) throw new Error('No idToken returned');
        onSuccess(idToken);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        const isCancelled = msg.includes('cancelled') || msg.includes('cancel') || msg.includes('12501') || msg.includes('12502');
        console.error('[GoogleSignIn] Native sign-in error', msg, err);
        if (!isCancelled) {
          onError?.(err);
        }
      } finally {
        btn.disabled = false;
      }
    });

    element.appendChild(btn);
  }

  private renderWebButton(element: HTMLElement, onSuccess: (idToken: string) => void): void {
    const clientId = environment.googleClientId;
    if (!clientId) {
      console.warn('[GoogleSignIn] googleClientId is not set in environment.');
      return;
    }

    const init = () => {
      if (!this.webInitialized) {
        window.google?.accounts?.id?.initialize({
          client_id: clientId,
          callback: (res) => onSuccess(res.credential),
          auto_select: false,
        });
        this.webInitialized = true;
      }
      this.doWebRender(element);
    };

    if (typeof window !== 'undefined' && window.google?.accounts?.id) {
      init();
    } else {
      const check = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(check);
          init();
        }
      }, 100);
      setTimeout(() => clearInterval(check), 10000);
    }
  }

  private doWebRender(element: HTMLElement): void {
    element.innerHTML = '';
    const theme = this.getCurrentTheme() === 'dark' ? 'filled_black' : 'outline';
    window.google?.accounts?.id?.renderButton(element, {
      type: 'standard',
      theme,
      size: 'large',
      text: 'continue_with',
      width: element.offsetWidth || 320,
    });
  }
}
